/**
 * Schema overrides
 *
 * NOTE: Like our operation overrides, this is a temporary solution to get the types
 * we need. Ideally, the OpenAPI spec would be our source of truth. But sometimes that
 * is not feasible.
 *
 * This file uses TypeScript declaration merging to override schema types.
 * The generated openapi.ts file uses `SchemaRefs` for all schema references,
 * which defaults to extending `BaseSchemas`. Here we augment `SchemaRefs` to apply
 * frontend-specific type modifications.
 *
 * To add a new override:
 * - Add your override in the `SchemaRefs` interface augmentation
 * - Use `BaseSchemas` to reference the original type
 *
 * To add a new type that doesn't exist in OpenAPI:
 * 1. Define the type here or import it
 * 2. Add it to `SchemaRefs` interface - it will be accessible via schemas['TypeName']
 */

import type { BaseSchemas } from './generated/openapi';

// Custom segment types (renamed from UserSegment, UserSegmentRule, etc.)
export type Segment = BaseSchemas['UserSegment'];

export type SegmentRule = Omit<BaseSchemas['UserSegmentRule'], 'clauses'> & {
  _key?: string; // We use _key instead of id for new rules
  clauses: SegmentRuleClause[];
};

export type SegmentRuleClause = BaseSchemas['Clause'] & {
  _key?: string; // We use _key instead of id for new clauses
};

export type SegmentSemanticInstruction =
  | AddBigSegmentIncludedTargetsInstruction
  | AddClausesToRuleInstruction
  | AddExcludedTargetsInstruction
  | AddExcludedUsersInstruction
  | AddExpiringTargetInstruction
  | AddIncludedTargetsInstruction
  | AddIncludedUsersInstruction
  | AddRuleInstruction
  | AddValuesToClauseInstruction
  | ProcessBigSegmentImportInstruction
  | RemoveBigSegmentIncludedTargetsInstruction
  | RemoveExcludedTargetsInstruction
  | RemoveExcludedUsersInstruction
  | RemoveExpiringTargetInstruction
  | RemoveIncludedTargetsInstruction
  | RemoveClausesFromRuleInstruction
  | RemoveIncludedUsersInstruction
  | RemoveRuleInstruction
  | RemoveValuesFromClauseInstruction
  | ReorderRulesInstruction
  | UpdateClauseInstruction
  | UpdateExpiringTargetInstruction
  | UpdateRuleDescriptionInstruction
  | UpdateRuleRolloutInstruction;

export type AddBigSegmentIncludedTargetsInstruction = {
  kind: 'addBigSegmentIncludedTargets';
  values: string[];
};

export type AddClausesToRuleInstruction = {
  kind: 'addClauses';
  ruleId: string;
  clauses: SegmentRuleClause[];
};

export type AddExcludedTargetsInstruction = {
  kind: 'addExcludedTargets';
  contextKind: string;
  values: string[];
};

export type AddExcludedUsersInstruction = {
  kind: 'addExcludedUsers';
  values: string[];
};

export type AddExpiringTargetInstruction = {
  kind: 'addExpiringTarget';
  value: number;
  targetType: string;
  contextKey: string;
  contextKind: string;
};

export type AddIncludedTargetsInstruction = {
  kind: 'addIncludedTargets';
  contextKind: string;
  values: string[];
};

export type AddIncludedUsersInstruction = {
  kind: 'addIncludedUsers';
  values: string[];
};

export type AddRuleInstruction = {
  kind: 'addRule';
  beforeRuleId?: string;
} & SegmentRule;

export type AddValuesToClauseInstruction = {
  kind: 'addValuesToClause';
  ruleId: string;
  clauseId: string;
  values: unknown[];
};

export type ProcessBigSegmentImportInstruction = {
  kind: 'processBigSegmentImport';
  importId: string;
};

export type RemoveBigSegmentIncludedTargetsInstruction = {
  kind: 'removeBigSegmentIncludedTargets';
  values: string[];
};

export type RemoveClausesFromRuleInstruction = {
  kind: 'removeClauses';
  ruleId: string;
  clauseIds: string[];
};

export type RemoveExcludedTargetsInstruction = {
  kind: 'removeExcludedTargets';
  contextKind: string;
  values: string[];
};

export type RemoveExcludedUsersInstruction = {
  kind: 'removeExcludedUsers';
  values: string[];
};

export type RemoveExpiringTargetInstruction = {
  kind: 'removeExpiringTarget';
  targetType: string;
  contextKey: string;
  contextKind: string;
};

export type RemoveIncludedTargetsInstruction = {
  kind: 'removeIncludedTargets';
  contextKind: string;
  values: string[];
};

export type RemoveIncludedUsersInstruction = {
  kind: 'removeIncludedUsers';
  values: string[];
};

export type RemoveRuleInstruction = {
  kind: 'removeRule';
  ruleId: string;
};

export type RemoveValuesFromClauseInstruction = {
  kind: 'removeValuesFromClause';
  clauseId: string;
  ruleId: string;
  values: unknown[];
};

export type ReorderRulesInstruction = {
  kind: 'reorderRules';
  ruleIds: string[];
};

export type UpdateClauseInstruction = {
  kind: 'updateClause';
  ruleId: string;
  clauseId: string;
  clause: SegmentRuleClause;
};

export type UpdateExpiringTargetInstruction = {
  kind: 'updateExpiringTarget';
  value: number;
  targetType: string;
  contextKey: string;
  contextKind: string;
  version: number;
};

export type UpdateRuleDescriptionInstruction = {
  kind: 'updateRuleDescription';
  ruleId: string;
  description: string;
};

type UpdateRuleRolloutInstructionBase = {
  kind: 'updateRuleRolloutAndContextKind';
  ruleId: string;
};

export type UpdateRuleRolloutInstruction = UpdateRuleRolloutInstructionBase &
  (
    | {
        weight: number;
        contextKind: string;
        resetRollout?: undefined;
      }
    | { weight?: number; contextKind: string; resetRollout?: undefined }
    | { weight: number; contextKind?: string; resetRollout?: undefined }
    | { weight?: undefined; contextKind?: undefined; resetRollout: true }
  );

// Provide a stable alias for the OpenAPI BillingProcess schema to avoid
// colliding with the custom BillingProcess enum exported elsewhere.
export type BillingProcessSchema = BaseSchemas['BillingProcess'];
export type ResourceCategorySchema = BaseSchemas['ResourceCategory'];

export enum PlanTypes {
  LEGACY = 'Legacy',
  STARTER = 'Starter',
  STARTER2021 = 'Starter2021',
  STANDARD_TRIAL = 'StandardTrial',
  STANDARD_TRIAL2021 = 'StandardTrial2021',
  PROFESSIONAL = 'Professional',
  PROFESSIONAL2021 = 'Professional2021',
  ENTERPRISE_TRIAL = 'EnterpriseTrial',
  ENTERPRISE = 'Enterprise',
  ENTERPRISE2023 = 'Enterprise2023',
  FOUNDATION2023 = 'Foundation2023',
  FOUNDATION_TRIAL2023 = 'FoundationTrial2023',
  PARTNER = 'Partner',
  /**
   * @deprecated Use FOUNDATION2023 instead with BillingIntervals.MONTHLY instead
   */
  DEVELOPER = 'Developer',
  GUARDIAN2024 = 'Guardian2024',
  GUARDIAN_SEATS2024 = 'Guardian2024S',
  GUARDIAN_TRIAL2024 = 'GuardianTrial2024',
  DEVELOPER2025 = 'Developer2025',
  FOUNDATION2025 = 'Foundation2025',
}

export enum BillingIntervals {
  YEARLY = 'AnnualBilling',
  MONTHLY = 'MonthlyBilling',
}

export enum BillingProcess {
  STANDARD = 'StandardBilling',
  USAGE_BASED_BILLING = 'UsageBasedBilling',
  COMMIT_WITH_OVERAGE = 'CommitWithOverageBilling',
  INVOICED = 'InvoicedBilling',
}

export enum LimitNames {
  SEATS = 'SeatCount',
  EVENTS_PUBLISHED = 'EventsPublished',
  EVENTS_RECEIVED = 'EventsReceived',
  SSO = 'SSO',
  EXPERIMENTATION_KEYS = 'ExperimentationKeys',
  MONTHLY_ACTIVE_USERS = 'MonthlyActiveUsers',
  HOSTS = 'Hosts',
  EXPERIMENTATION_MAU = 'ExperimentationMAU',
  SERVICE_CONNECTIONS = 'ServiceConnections',
  SESSIONS = 'Sessions',
  ERRORS = 'Errors',
  LOGS = 'Logs',
  TRACES = 'Traces',
  AI_RUNS = 'AIRuns',
  VEGA_AI = 'VegaAI',
}

export type PlanLimitsType = {
  [K in LimitNames]: number | null;
};

export type PutSubscriptionRequestBody = {
  planType: PlanTypes;
  billingInterval: BillingIntervals;
  billingProcess?: BillingProcess;
  limits?: PlanLimitsType;
  version?: number;
  metadata?: BaseSchemas['SubscriptionMetadata'];
  prorationTime?: BaseSchemas['InternalSubscriptionPutRep']['prorationTime'];
  stripeToken?: BaseSchemas['InternalSubscriptionPutRep']['stripeToken'];
  serviceConnectionAttestation?: BaseSchemas['ServiceConnectionAttestationRep'];
};

export type ResourceCategory = 'any' | 'organization' | 'project';

export type RoleName = 'no_access' | 'reader' | 'writer' | 'owner' | 'admin' | 'custom' | 'inline';

export type PostSubscriptionPreviewRequestBody = {
  planType: PlanTypes | null;
  billingInterval: BillingIntervals | null;
  billingProcess?: BillingProcess | null;
  version?: number;
  // Missing keys = "not configured" (see `SubscriptionPutRep.GetLimits`).
  limits: Partial<PlanLimitsType>;
  serviceConnectionAttestation?: BaseSchemas['ServiceConnectionAttestationRep'];
};

export enum UsageStatus {
  usageWarning = 'usageWarning',
  usageExceeded = 'usageExceeded',
}

export type SubscriptionUsageStatus = Omit<BaseSchemas['SubscriptionUsageStatusResponse'], '_usageStatus'> & {
  _usageStatus?: Partial<Record<LimitNames, UsageStatus>>;
};

// Once the SubscriptionRep has the _next field returning from the spec, update this
export type ScheduledSubscriptionChange = {
  effectiveDate: string;
  updatedAt: string;
  primaryContext: string;
  limits: { [k in LimitNames]: number };
};

export enum SubscriptionTypes {
  STANDARD = 'Standard',
  EMPLOYEE = 'Employee',
  TEST = 'Test',
  SECURITY = 'Security',
  PARTNER = 'Partner',
  MARKETPLACE = 'Marketplace',
}

export enum MarketplaceProviders {
  VERCEL = 'Vercel',
  AWS = 'AWS',
}

// state, planType, billingInterval, billingProcess, marketplaceProvider, and subscriptionType in the BE are enums
// but in the API response their string representation is returned
export type ModernSubscription = Omit<
  BaseSchemas['ModernSubscription'],
  'state' | 'planType' | 'billingInterval' | 'billingProcess' | '_limits' | 'marketplaceProvider' | 'subscriptionType'
> & {
  state?: string;
  planType?: PlanTypes;
  billingInterval?: BillingIntervals;
  billingProcess?: BillingProcess;
  _limits?: { [k in LimitNames]?: number | null };
  _next?: ScheduledSubscriptionChange | null;
  subscriptionType?: SubscriptionTypes;
  marketplaceProvider?: MarketplaceProviders;
};

// 1. validate that treatments have a valid analysis model
// 2. adds a methodology and pdf field to each treatment
// 3. renames (bayesian|frequentist)(Beta|Normal) to modelAnalysis
export type TreatmentAnalysis = Omit<
  BaseSchemas['TreatmentAnalysisRep'],
  'bayesianBeta' | 'bayesianNormal' | 'frequentistBeta' | 'frequentistNormal'
> &
  (
    | {
        model: 'bayesianBeta';
        methodology: 'bayesian';
        pdf: 'beta';
        modelAnalysis: BaseSchemas['BayesianBetaAnalysisRep'];
      }
    | {
        model: 'bayesianNormal';
        methodology: 'bayesian';
        pdf: 'normal';
        modelAnalysis: BaseSchemas['BayesianNormalAnalysisRep'];
      }
    | {
        model: 'frequentistBeta';
        methodology: 'frequentist';
        pdf: 'beta';
        modelAnalysis: BaseSchemas['FrequentistBetaAnalysisRep'];
      }
    | {
        model: 'frequentistNormal';
        methodology: 'frequentist';
        pdf: 'normal';
        modelAnalysis: BaseSchemas['FrequentistNormalAnalysisRep'];
      }
  );

export type ExperimentAnalysis = Omit<BaseSchemas['ExperimentAnalysisRep'], 'treatments'> & {
  treatments: TreatmentAnalysis[];
};

// TODO(WHI-315): Remove this override once we release the other data sources in the public API spec.
// `databricks`, `redshift`, `bigquery`, and `clickhouse` are internal data sources excluded from
// the public API spec but still valid values that the frontend needs to handle.
export type Experiment = Omit<BaseSchemas['Experiment'], 'dataSource'> & {
  dataSource?: 'launchdarkly' | 'snowflake' | 'databricks' | 'redshift' | 'bigquery' | 'clickhouse';
};

export type ExperimentPost = Omit<BaseSchemas['ExperimentPost'], 'dataSource'> & {
  dataSource?: 'launchdarkly' | 'snowflake' | 'databricks' | 'redshift' | 'bigquery' | 'clickhouse';
};

export type ExperimentInfoAnalysisRep = Omit<BaseSchemas['ExperimentInfoAnalysisRep'], 'dataSource'> & {
  dataSource?: 'launchdarkly' | 'snowflake' | 'databricks' | 'redshift' | 'bigquery' | 'clickhouse';
};

export type ExperimentResultsLastUpdatedRep = Omit<BaseSchemas['ExperimentResultsLastUpdatedRep'], 'dataSource'> & {
  dataSource?: 'launchdarkly' | 'snowflake' | 'databricks' | 'redshift' | 'bigquery' | 'clickhouse';
};

// TODO: Remove this override once ClickHouse is released and added to the public API spec.
// `clickhouse` is excluded from the public/hidden API spec but is a valid destination kind
// that the frontend needs to handle.
export type Destination = Omit<BaseSchemas['Destination'], 'kind'> & {
  kind?:
    | 'google-pubsub'
    | 'kinesis'
    | 'mparticle'
    | 'segment'
    | 'azure-event-hubs'
    | 'snowflake-v2'
    | 'databricks'
    | 'bigquery'
    | 'redshift'
    | 'clickhouse';
};

export type DestinationPost = Omit<BaseSchemas['DestinationPost'], 'kind'> & {
  kind?:
    | 'google-pubsub'
    | 'kinesis'
    | 'mparticle'
    | 'segment'
    | 'azure-event-hubs'
    | 'snowflake-v2'
    | 'databricks'
    | 'bigquery'
    | 'redshift'
    | 'clickhouse';
};

// Custom approval settings type - allows specific known service kinds plus any string
export type ApprovalSettingsServiceKind = 'launchdarkly' | 'servicenow' | 'custom-approval' | (string & {});

// SDK EOL Status enum for type safety
export enum EolStatus {
  ALL_CLEAR = 'EolAllClear',
  MAJOR_VERSION_AVAILABLE = 'MajorVersionAvailable',
  EOL_NEAR = 'EolNear',
  EOL_APPROACHING = 'EolApproaching',
  EOL_PAST = 'EolPast',
  DEPRECATED = 'Deprecated',
  UNKNOWN = 'EolUnknown',
}

// Override SdkVersionDetailsRep to use EolStatus enum instead of string
export type SdkVersionDetailsRep = Omit<BaseSchemas['SdkVersionDetailsRep'], 'eolStatus' | 'relayEolStatus'> & {
  eolStatus?: EolStatus;
  relayEolStatus?: EolStatus;
};

export type SdkKey = BaseSchemas['SdkKey'] & {
  environmentSummary?: BaseSchemas['EnvironmentSummary'];
  // viewSummaries is no longer part of the base SdkKey schema (the get/post/patch
  // endpoints never returned it). The client-side SdkKey is a composite fed by
  // getProjectSdkKeys (SdkKeyListItem), which still carries viewSummaries, so we
  // retain the field here for ManageSdkKey/EditSdkKeyModal.
  viewSummaries?: Array<BaseSchemas['ViewSummary']>;
};

/** Chart-data suggestion requests accept the same filter clauses as chart-data queries. */
export type SuggestionRequest = BaseSchemas['SuggestionRequest'] & {
  filters?: BaseSchemas['Filters'];
};

// The AI Config variation `model` is declared as a free-form `type: object` in the spec, which
// openapi-typescript renders as the unusable `Record<string, never>`. The backend's
// `parseRequestModel` reads exactly these four keys and the frontend sends exactly these, so model
// the real wire shape here. `parameters`/`custom` hold `unknown` rather than JSON primitives: both
// are `map[string]interface{}` on the backend and both are authored as raw JSON in the UI, so nested
// objects and arrays are legal — `{"thinking": {"type": "enabled", "budget_tokens": 1024}}`,
// `{"stop_sequences": ["END"]}`, and the string array LaunchDarkly itself writes to
// `custom.evaluationConfig`. `model` stays optional, matching the spec (not in `required`) and Go
// `*ModelParameters`.
type AIConfigModelJsonObject = Record<string, unknown>;
type AIConfigVariationModel = {
  modelName?: string;
  parameters?: AIConfigModelJsonObject;
  custom?: AIConfigModelJsonObject;
  region?: string;
};

export type AIConfigClassifier = {
  key: string;
  type: 'bool' | 'float' | 'enum';
  instructions: string;
  criteria?: Record<string, string | null> | string[] | { true?: string; false?: string };
};

export type AIConfigVariation = Omit<BaseSchemas['AIConfigVariation'], 'classifiers'> & {
  classifiers?: AIConfigClassifier[];
};

export type AIConfigVariationPost = Omit<BaseSchemas['AIConfigVariationPost'], 'model' | 'classifiers'> & {
  model?: AIConfigVariationModel;
  classifiers?: AIConfigClassifier[];
};

export type AIConfigVariationPatch = Omit<BaseSchemas['AIConfigVariationPatch'], 'model' | 'classifiers' | 'state'> & {
  model?: AIConfigVariationModel;
  classifiers?: AIConfigClassifier[];
  state?: string;
};

export type SignupOutcome = 'existing_account_notified';

export type UnverifiedMember = Omit<BaseSchemas['UnverifiedMember'], 'signupOutcome'> & {
  signupOutcome?: SignupOutcome;
};

// `POST /flags` accepts `templateKey` (one of `custom`, `kill-switch`, `release`, `experiment`,
// `migration`) and uses it both to fill in fields the body omits and to attribute the flag to a
// template in analytics, but the field is hidden from the published spec.
export type FeatureFlagBodyWithTemplateKey = BaseSchemas['FeatureFlagBody'] & {
  templateKey?: string | null;
};

// The beta dependent-flags response includes archive metadata that is intentionally
// omitted from the published OpenAPI schema.
export type MultiEnvironmentDependentFlag = BaseSchemas['MultiEnvironmentDependentFlag'] & {
  archived?: boolean;
  archivedDate?: number;
};

export type IterationAttributeSourceRep = {
  key: string;
  sourceId: string;
};

export type IterationRep = BaseSchemas['IterationRep'] & {
  attributeSources?: IterationAttributeSourceRep[];
};

/*
 * Define any modifications to generated types here.
 * Use this power wisely and consider making changes to the OpenAPI schema itself whenever possible.
 *
 * Make sure to omit the overridden types in `core-types` to ensure everything works as expected.
 */

declare module './generated/openapi' {
  interface SchemaRefs {
    Segment: Segment;
    SegmentRule: SegmentRule;
    SegmentRuleClause: SegmentRuleClause;
    SegmentSemanticInstruction: SegmentSemanticInstruction;
    UserSegment: Segment;
    UserSegmentRule: SegmentRule;
    Clause: SegmentRuleClause;
    PatchSegmentInstruction: SegmentSemanticInstruction;
    SubscriptionType: SubscriptionTypes;
    MarketplaceProviders: MarketplaceProviders;
    SubscriptionUsageStatusResponse: SubscriptionUsageStatus;
    ModernSubscription: ModernSubscription;
    TreatmentAnalysisRep: TreatmentAnalysis;
    ExperimentAnalysisRep: ExperimentAnalysis;
    Experiment: Experiment;
    ExperimentPost: ExperimentPost;
    ExperimentInfoAnalysisRep: ExperimentInfoAnalysisRep;
    ExperimentResultsLastUpdatedRep: ExperimentResultsLastUpdatedRep;
    SdkVersionDetailsRep: SdkVersionDetailsRep;
    Destination: Destination;
    DestinationPost: DestinationPost;
    SdkKey: SdkKey;
    SuggestionRequest: SuggestionRequest;
    AIConfigVariation: AIConfigVariation;
    AIConfigVariationPost: AIConfigVariationPost;
    AIConfigVariationPatch: AIConfigVariationPatch;
    UnverifiedMember: UnverifiedMember;
    FeatureFlagBody: FeatureFlagBodyWithTemplateKey;
    MultiEnvironmentDependentFlag: MultiEnvironmentDependentFlag;
    IterationAttributeSourceRep: IterationAttributeSourceRep;
    IterationRep: IterationRep;
  }
}
